import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

export const IMPERSONATOR_TOKEN_KEY = 'impersonator_token';
export const IMPERSONATION_INFO_KEY = 'impersonation_info';

export interface ImpersonationInfo {
  name: string;
  email: string;
  companyName: string;
  startedAt: string;
}

/** Start impersonating: keep the App Owner's own token aside, then switch to the user's. */
export const beginImpersonation = (token: string, info: ImpersonationInfo) => {
  const current = sessionStorage.getItem('auth_token');
  if (current && !sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY)) {
    sessionStorage.setItem(IMPERSONATOR_TOKEN_KEY, current);
  }
  sessionStorage.setItem(IMPERSONATION_INFO_KEY, JSON.stringify(info));
  sessionStorage.setItem('auth_token', token);
  window.location.href = '/';
};

/** Stop impersonating and return to the App Owner's own session. */
export const endImpersonation = async () => {
  try {
    await api.post('/impersonation/stop');
  } catch {
    // The session may already have expired; restoring the owner's token is what matters.
  }
  const original = sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY);
  sessionStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
  sessionStorage.removeItem(IMPERSONATION_INFO_KEY);
  if (original) {
    sessionStorage.setItem('auth_token', original);
    window.location.href = '/manage/companies';
  } else {
    sessionStorage.removeItem('auth_token');
    window.location.href = '/login';
  }
};

export const clearImpersonation = () => {
  sessionStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
  sessionStorage.removeItem(IMPERSONATION_INFO_KEY);
};

const ImpersonationBanner: React.FC = () => {
  const [info, setInfo] = useState<ImpersonationInfo | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem(IMPERSONATOR_TOKEN_KEY)) return;
    try {
      setInfo(JSON.parse(sessionStorage.getItem(IMPERSONATION_INFO_KEY) || 'null'));
    } catch {
      setInfo(null);
    }
  }, []);

  useEffect(() => {
    if (!info) return;
    document.body.style.paddingTop = '44px';
    return () => {
      document.body.style.paddingTop = '';
    };
  }, [info]);

  if (!info) return null;

  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-4 bg-amber-500 text-black px-4 h-[44px] text-sm shadow"
    >
      <span>
        You are signed in as <strong>{info.name}</strong> ({info.email})
        {info.companyName ? <> at <strong>{info.companyName}</strong></> : null}. Everything you do is recorded under your own name.
      </span>
      <button
        onClick={async () => {
          setLeaving(true);
          await endImpersonation();
        }}
        disabled={leaving}
        className="px-3 py-1 rounded bg-black text-white font-medium hover:bg-gray-800 disabled:opacity-60"
      >
        {leaving ? 'Returning…' : 'Exit impersonation'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
